import React, { useCallback, useMemo, useState } from 'react';
import { splitColorPickerColorTokens } from '@/utils/splitColorPickerColorTokens';
import { getDefaultButtonColor } from '@/config/theme';

export type ColorPickerSelectHandler = (
  variableName: string,
  colorValue: string,
  targetScene: string
) => void | Promise<void>;

export interface ColorPickerFieldProps {
  variableName: string;
  /** Raw `colors` attribute from `<Color-Picker>` (comma-separated CSS tokens). */
  colors: string;
  selectionTarget: string;
  /**
   * Adventure option / button color for the outer frame (same convention as scene options).
   * When omitted, {@link getDefaultButtonColor} is used so the frame always matches the option palette.
   */
  frameColor?: string;
  disabled?: boolean;
  onSelect: ColorPickerSelectHandler;
  className?: string;
}

/**
 * Swatch-only color picker: one button per parsed color token (no visible text).
 */
const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  variableName,
  colors,
  selectionTarget,
  frameColor,
  disabled = false,
  onSelect,
  className = ''
}) => {
  const tokens = useMemo(() => splitColorPickerColorTokens(colors), [colors]);
  const [pending, setPending] = useState(false);

  const optionFrameColor = frameColor ?? getDefaultButtonColor();
  /** Match glass `OptionButton` / inline-option frame: visible border + shadow (1px/40 alone is invisible on swatches). */
  const frameCssVars = useMemo((): React.CSSProperties => {
    const f = optionFrameColor;
    const borderMuted = f.startsWith('#') ? `${f}99` : f;
    const borderStrong = f.startsWith('#') ? `${f}CC` : f;
    return {
      ['--picker-option-border' as string]: borderMuted,
      ['--picker-option-border-hover' as string]: borderStrong
    };
  }, [optionFrameColor]);

  const handlePick = useCallback(
    (token: string) => {
      if (disabled || pending) {
        return;
      }
      setPending(true);
      void (async () => {
        try {
          await onSelect(variableName, token, selectionTarget);
        } finally {
          setPending(false);
        }
      })();
    },
    [disabled, pending, onSelect, variableName, selectionTarget]
  );

  if (tokens.length === 0) {
    return null;
  }

  const busy = disabled || pending;

  return (
    <div
      className={`rich-text-color-picker ${className}`.trim()}
      role="group"
      aria-label="Color choices"
      aria-busy={busy}
      style={frameCssVars}
    >
      {tokens.map((token, i) => (
        <button
          key={`${token}-${i}`}
          type="button"
          className="rich-text-color-picker__swatch"
          style={{ backgroundColor: token }}
          aria-label={token}
          disabled={busy}
          onClick={() => handlePick(token)}
        />
      ))}
    </div>
  );
};

export default ColorPickerField;
